import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface ExportOptions {
  element: HTMLElement;
  width: number;
  height: number;
  scale?: number;
  quality?: number;
  filename?: string;
}

export async function exportToPNG(options: ExportOptions): Promise<Blob> {
  const { element, scale = 2 } = options;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: null,
    logging: false,
    useCORS: true,
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/png');
  });
}

export async function exportToJPEG(options: ExportOptions): Promise<Blob> {
  const { element, scale = 2, quality = 0.92 } = options;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: '#12161F', // Navy background for JPEG (no transparency)
    logging: false,
    useCORS: true,
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', quality);
  });
}

export async function exportToPDF(options: ExportOptions): Promise<Blob> {
  const { element, width, height, scale = 2 } = options;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: null,
    logging: false,
    useCORS: true,
  });

  const orientation = width > height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({
    orientation,
    unit: 'px',
    format: [width * scale, height * scale],
  });

  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', 0, 0, width * scale, height * scale);

  return pdf.output('blob');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportAndDownload(
  format: 'png' | 'jpeg' | 'pdf',
  options: ExportOptions
): Promise<void> {
  const { filename = 'export' } = options;
  let blob: Blob;
  let ext: string;

  switch (format) {
    case 'png':
      blob = await exportToPNG(options);
      ext = '.png';
      break;
    case 'jpeg':
      blob = await exportToJPEG(options);
      ext = '.jpg';
      break;
    case 'pdf':
      blob = await exportToPDF(options);
      ext = '.pdf';
      break;
  }

  downloadBlob(blob, filename + ext);
}

/**
 * Capture a thumbnail of the canvas as a data URL
 * Used for creative preview cards
 */
export async function captureThumbnail(
  element: HTMLElement,
  maxSize: number = 200
): Promise<string> {
  // Calculate scale to fit within maxSize
  const aspectRatio = element.offsetWidth / element.offsetHeight;
  let width: number, height: number;

  if (aspectRatio > 1) {
    width = maxSize;
    height = maxSize / aspectRatio;
  } else {
    height = maxSize;
    width = maxSize * aspectRatio;
  }

  const scale = width / element.offsetWidth;

  const canvas = await html2canvas(element, {
    scale,
    backgroundColor: '#12161F',
    logging: false,
    useCORS: true,
  });

  return canvas.toDataURL('image/jpeg', 0.7);
}
